// The Energy screen's orders — every rule, with no screen in them
// (tests/energy-screen/run.mjs; tests/energy-screen/mutations.mjs breaks each
// one and requires a check to fail).
//
// One file, on purpose: these are checked by plain Node, which cannot follow
// an import from one of the app's TypeScript files to another.
//
// PART ONE — what the screen SAYS about an order.
//
// Each rule here closes something a review found on 1 October 2026, in a
// screen whose first version had never been put in front of a reviewer:
//
//   1. AN ANSWER IS ONE OF THREE THINGS, NOT TWO. The desk says `ok: false` for
//      an order it refused — and also for one that WENT OUT and got no answer,
//      and for one that was placed and could not be written down. The first
//      version printed "Not rented" for all three. An order that may have been
//      charged is not "not rented".
//   2. THE PRICE THE BUTTON SHOWS IS THE PRICE THE ORDER IS HELD TO, to the
//      sun. The button said "Rent for 4.371 TRX" and sent a ceiling of
//      4.371250: what was approved was not what was shown.
//   3. "ARRANGE IT NOW" RENTS THE FIGURE THAT IS SAVED. The field beside it can
//      be edited; the desk knows only what was saved. With an unsaved edit on
//      screen, the button rented a figure the operator was no longer looking at.

//   4. WHAT COMES BACK IS CHECKED, NOT TRUSTED. The client casts the desk's
//      answer to a type; a cast is a promise, not a check. `{}` read as a
//      refusal and `{ ok: "false" }` as an order — and either one spent the key
//      and put the warning away. An answer that is not shaped like an answer
//      TO THIS ORDER is no answer: the key and the warning stay.
//   5. "YES" TO A KEY THE DESK HAS SEEN BEFORE PLACES NOTHING. The desk answers
//      a repeated key with the rental it already made — which by then may be
//      delivered, ended, or settled by hand. The screen said "Ordered", dated
//      now, and promised a delivery. It says what that rental is instead.

/** The parts of a rental this file reads. (The screen's own type has the rest.) */
export interface OrderedRental { id: number; walletId: number; requestKey: string; state: string; createdAt: string | null }
/** What was sent: enough to tell whether a rental in the answer is THIS order's. */
export interface SentOrder { requestKey: string; walletId: number }

export type OrderOutcome =
  /** Placed by THIS press. */
  | { kind: 'ordered'; rentalId: number }
  /**
   * The desk had this order already — this press placed nothing. `state` is
   * what that rental was when the desk answered; the screen shows what it is
   * NOW from the list it is drawing. `createdAt`: when the desk wrote it down.
   */
  | { kind: 'already'; rentalId: number; state: string; createdAt: string | null }
  | { kind: 'nothing'; reason: string }
  /** Definitely not placed: the desk declined, or the provider refused. Nothing was bought. */
  | { kind: 'not_rented'; reason: string }
  /** It may have been placed — and charged. The desk does not know, and neither does this screen. */
  | { kind: 'unknown'; reason: string; rentalId: number; state: string };

const STATES_NOTHING_WAS_BOUGHT_IN = new Set(['refused']);
/** The states a rental can be in when the desk has just said "yes, placed". */
const STATES_OF_A_PLACED_ORDER = new Set(['ordered', 'delegated']);

/**
 * A rental as it came off the wire — or null when it is not shaped like one.
 * (Called with something that is neither null nor undefined. Text, a number, a
 * list: none of them has a numeric `id`, and that is the first thing asked.)
 */
function rentalOf(v: unknown): OrderedRental | null {
  const r = v as Record<string, unknown>;
  if (typeof r.id !== 'number' || !Number.isSafeInteger(r.id) || r.id <= 0) return null;
  if (typeof r.walletId !== 'number' || !Number.isSafeInteger(r.walletId)) return null;
  if (typeof r.requestKey !== 'string' || typeof r.state !== 'string' || r.state === '') return null;
  const createdAt = typeof r.createdAt === 'string' && Number.isFinite(Date.parse(r.createdAt)) ? r.createdAt : null;
  return { id: r.id, walletId: r.walletId, requestKey: r.requestKey, state: r.state, createdAt };
}

/**
 * What became of an order, from what the desk sent back — or NULL when that is
 * not an answer to this order at all. Null is "no answer": the caller keeps the
 * key and the warning, exactly as if nothing had come back.
 */
export function orderOutcome(answer: unknown, sent: SentOrder): OrderOutcome | null {
  if (answer === null || answer === undefined) return null;
  const a = answer as Record<string, unknown>;
  // A decision says yes or no. `{}` is not a refusal, and "false" is not a yes.
  // (Text, a number, a list: none of them has a yes or a no either.)
  if (typeof a.ok !== 'boolean') return null;
  const reason = typeof a.reason === 'string' && a.reason !== '' ? a.reason : 'the desk gave no reason';
  // A rental that came with the answer and cannot be read tells nothing — about this order or any other.
  const given = a.rental !== undefined && a.rental !== null;
  const rental = given ? rentalOf(a.rental) : null;
  if (given && rental === null) return null;
  // A refusal may come with a rental that is NOT this order's — "that wallet
  // already has one in progress" hands back the one in the way. Only a rental
  // written down for THIS request says anything about what became of it.
  const mine = rental !== null && rental.requestKey === sent.requestKey && rental.walletId === sent.walletId ? rental : null;

  // "Seen before", when it is said at all, is said with a yes or a no.
  if (a.replayed !== undefined && typeof a.replayed !== 'boolean') return null;

  if (a.ok) {
    if (a.nothingToDo === true) return { kind: 'nothing', reason };
    // "Yes" is about this order only when this order's rental comes with it.
    if (mine === null) return null;
    // The desk had seen this key before: nothing was placed by this press.
    // Whatever that rental is by now — still being sent, delivered, ended,
    // settled by hand — it is not "ordered, and on its way".
    if (a.replayed === true) return { kind: 'already', rentalId: mine.id, state: mine.state, createdAt: mine.createdAt };
    // Placed now — and then the rental is in a state a placed order can be in. A "yes" beside anything else is not an answer this screen can read.
    if (!STATES_OF_A_PLACED_ORDER.has(mine.state)) return null;
    return { kind: 'ordered', rentalId: mine.id };
  }
  // Written down for this request, and not marked refused: it went out, or may
  // have. Whatever the words that came with it, that is not "not rented".
  if (mine !== null && !STATES_NOTHING_WAS_BOUGHT_IN.has(mine.state)) {
    return { kind: 'unknown', reason, rentalId: mine.id, state: mine.state };
  }
  return { kind: 'not_rented', reason };
}

/**
 * Sun → TRX as the energy screens say it (the house style, rule 10 — the operator, 29 Oct 2026: "Crypto 4-5 no, crypto
 * also keep 2"; of the screens, N-084, 30 Oct 2026: "screens too"): at most two places, rounded half up on the sun
 * itself, trailing zeros dropped, grouped in thousands — 4,371,250 sun is 4.37. One that is not nothing and rounds to
 * nothing is "under 0.01", never 0. Anything but digits is "—". The order button's price too: the order still carries
 * the quote to the sun as its ceiling, and the desk refuses one that would cost more than that.
 */
export function trx(sun: string | null | undefined): string {
  if (typeof sun !== 'string' || !/^\d{1,18}$/.test(sun)) return '—';
  const v = BigInt(sun);
  const hundredths = (v + 5_000n) / 10_000n;     // 10,000 sun is 0.01 TRX; half of that rounds up
  if (hundredths === 0n) return v === 0n ? '0' : 'under 0.01';
  const fraction = (hundredths % 100n).toString().padStart(2, '0').replace(/0+$/, '');
  return `${Number(hundredths / 100n).toLocaleString('en-US')}${fraction ? `.${fraction}` : ''}`;
}

/**
 * Sun → TRX to the sun, for a price PER something: a day of a rental of days, a transfer's share of a standing estimate.
 * A rate is never rounded (N-090, the operator, 30 Oct 2026: "I didn't ask you to squeeze the rate decimals ever");
 * only amounts are (trx, above). Trailing zeros dropped, grouped in thousands; anything but digits is "—".
 */
export function rateTrx(sun: string | null | undefined): string {
  if (typeof sun !== 'string' || !/^\d{1,18}$/.test(sun)) return '—';
  const v = BigInt(sun);
  const fraction = (v % 1_000_000n).toString().padStart(6, '0').replace(/0+$/, '');
  return `${Number(v / 1_000_000n).toLocaleString('en-US')}${fraction ? `.${fraction}` : ''}`;
}

/**
 * Basis points → a saving as the screens say it: a percentage to one place at most, rounded half up, the trailing zero
 * dropped (rule 10) — 1,255 is 12.6%, 1,000 is 10%. One that is not nothing and rounds to nothing is "under 0.1%",
 * never 0%; below zero (renting dearer than burning), its size after a minus. Anything but a whole number is "—".
 */
export function percent(bps: number | null | undefined): string {
  if (typeof bps !== 'number' || !Number.isSafeInteger(bps)) return '—';
  const size = Math.abs(bps);
  const tenths = Math.floor((size + 5) / 10);     // 10 bps is 0.1%; half of that rounds up
  const sign = bps < 0 ? '-' : '';
  if (tenths === 0) return size === 0 ? '0%' : `${sign}under 0.1%`;
  return `${sign}${Math.floor(tenths / 10).toLocaleString('en-US')}${tenths % 10 === 0 ? '' : `.${tenths % 10}`}%`;
}

/** A standing figure as typed → digits, or null when it is not a whole number. */
export function standingDigits(typed: string): string | null {
  const digits = typed.replace(/[,\s]/g, '');
  return /^\d{1,9}$/.test(digits) ? String(Number(digits)) : null;
}

/**
 * Is the standing figure on screen something other than the one that is SAVED?
 * "Arrange it now" rents the saved one; while this is true it must not be offered.
 * Something that is not a number is unsaved too: it is certainly not what was saved.
 */
export function standingUnsaved(typed: string, saved: string): boolean {
  const digits = standingDigits(typed);
  return digits === null || digits !== String(Number(saved));
}

// =========================================================================
// PART TWO — the top-up panel.
//
// THE RULE: what is ordered is what was PRICED. The amount, the price and the
// key travel together from the moment the price comes back, and the order is
// built from them — never from whatever the form holds at the click.
//
// The panel's first version kept this in component state and a mutation hook,
// and four reviews in a row (1 October 2026) each found a way for the two to
// drift apart that nothing was checking:
//
//   - a price still on its way when the amount was edited came back and put a
//     quote for the OLD amount on screen;
//   - after an order got no answer, re-pricing left "pressing again sends the
//     same order" beside a button that would send a new one;
//   - editing the amount while an order was ON ITS WAY threw away its key and
//     detached the request, so its failure was never shown;
//   - closing the panel, opening another wallet's, or turning the phone threw
//     the whole state away — it lived in the panel.
//
// So a panel is one state and one function over it, and the states live ABOVE
// the panels (PART FOUR), one per wallet.
// =========================================================================

// =========================================================================
// THE CHOICES — energy by what it is FOR. (3 October 2026: the operator, whose
// payout wallets send 10 to 25 USDT transfers a day, asked for "more options
// in energy rental for longer duration and multiple no. of sends". The desk's
// own page has the same choices: energy/public/topup.js.)
//
// A USDT transfer takes about 65,000 energy to an address that already holds
// USDT, and about 131,000 to one that never has. A TOP-UP is energy for
// transfers now (rented for an hour). A STANDING figure is energy KEPT on a
// wallet: delegated energy refills over 24 hours, so energy for N transfers,
// kept, is N transfers EVERY DAY — however they fall in the day, since even N
// at once are covered — rented a few days at a time and renewed by the desk.
// =========================================================================

/** About what one USDT transfer takes, to an address that already holds USDT. */
export const SEND_ENERGY = 65_000n;

/** What a top-up offers: the energy the wallet should have, by the transfers it is for. */
export const TOP_UP_CHOICES = [
  { target: '65000', text: '1 USDT transfer — 65,000' },
  { target: '130000', text: '2 transfers — 130,000' },
  { target: '195000', text: '3 transfers — 195,000' },
  { target: '325000', text: '5 transfers — 325,000' },
  { target: '650000', text: '10 transfers — 650,000' },
  { target: '131000', text: '1 transfer to an address new to USDT — 131,000' },
] as const;

export type TopUpTarget = (typeof TOP_UP_CHOICES)[number]['target'] | 'other';

// =========================================================================
// THE TERMS — how long a top-up is rented for. (18 October 2026, the operator:
// "want longer duration rental enables as well, for 15 days-30 days".) The
// desk says which it takes (status.policy.topUpTerms); the screen offers
// those and nothing else. A term longer than a day is energy for a transfer
// EVERY DAY of it — delegated energy refills over 24 hours — and the desk
// compares it with burning one a day, as it does a standing figure.
// =========================================================================

/** The terms the desk takes for a top-up, as it says them — or, from a desk that says none, its one default term. */
export function topUpTerms(policy: { topUpDurationSec: number; topUpTerms?: readonly number[] }): number[] {
  const said = Array.isArray(policy.topUpTerms) ? policy.topUpTerms.filter((t) => Number.isSafeInteger(t) && t > 0) : [];
  return said.length > 0 ? [...new Set(said)].sort((a, b) => a - b) : [policy.topUpDurationSec];
}

/**
 * The term to SEND with a price and its order: the one on the panel — the desk's default when none was picked —
 * as seconds, whenever the desk lists its terms; null for a desk that lists none (it takes no term, and refuses one).
 * Never "whatever the default is when the order arrives": a default changed between the price and the order would
 * rent another length at the price shown (a review, 18 Oct 2026).
 */
export function termToSend(policy: { topUpDurationSec: number; topUpTerms?: readonly number[] }, picked: number | null): number | null {
  if (!Array.isArray(policy.topUpTerms) || policy.topUpTerms.length === 0) return null;
  return picked ?? policy.topUpDurationSec;
}

/**
 * What a rental of more than a day costs for each day it runs, in sun — over its ACTUAL length, whatever that is
 * (a review, 18 Oct 2026: a 36-hour rental was divided by two whole days, and its day looked cheaper than it is).
 * Null when there is no such figure: a day or less, or a price that is not digits.
 */
export function dailyCostSun(quote: { costSun: string | null; durationSec?: number }): bigint | null {
  const length = quote.durationSec;
  if (quote.costSun === null || !/^\d{1,18}$/.test(quote.costSun)) return null;
  if (typeof length !== 'number' || !Number.isSafeInteger(length) || length <= 86_400) return null;
  return (BigInt(quote.costSun) * 86_400n) / BigInt(length);
}

/**
 * How the chain's burn price stands: read and current; the last figure, kept while the reads fail; or never read.
 * A figure whose latest read failed is not "what burning costs" — it is what it cost when it was last read (the
 * same review: the screen's About said "Burning costs …" of a figure that could not be read).
 */
export function burnPriceState(burn: { energyFeeSun: string | null; error: string | null } | null | undefined): 'current' | 'last-known' | 'unread' {
  if (!burn || burn.energyFeeSun === null) return 'unread';
  return burn.error ? 'last-known' : 'current';
}

/** How long a rental runs, in words: "1 hour", "3 days", "15 days". */
export function termWords(seconds: number): string {
  if (seconds % 86_400 === 0) return `${seconds / 86_400} day${seconds === 86_400 ? '' : 's'}`;
  if (seconds % 3_600 === 0) return `${seconds / 3_600} hour${seconds === 3_600 ? '' : 's'}`;
  return `${Math.round(seconds / 60)} min`;
}

/** What a standing figure offers: transfers a day. */
export const SENDS_A_DAY = [5, 10, 15, 20, 25] as const;

/** The energy kept for `n` transfers a day. */
export const energyForSendsADay = (n: number): bigint => BigInt(n) * SEND_ENERGY;

/**
 * How many transfers a day a figure is — null unless it is a whole number of them. Read as Save reads it
 * (at most nine digits): anything longer is no figure at all, not a very large one — a pasted string of 300
 * digits made a number too large to count with, and the estimate threw while the figure was being typed
 * (a review, 3 October 2026). (The same rule as energy/public/topup.js sendsADayOf.)
 */
export function sendsADayOf(energy: string | bigint | null | undefined): number | null {
  const digits = String(energy).replace(/[,\s]/g, '');
  if (!/^\d{1,9}$/.test(digits)) return null;
  const e = BigInt(digits);
  return e > 0n && e % SEND_ENERGY === 0n ? Number(e / SEND_ENERGY) : null;
}

/** A price-board row, as far as an estimate needs one. */
interface BoardRow { purpose: string; durationSec: number; energy: string; costSun: string | null; problem: string | null }

export interface StandingEstimate { costSun: bigint; days: number; sends: number; perSendSun: bigint; burnSun: bigint | null; atIso: string }

/**
 * What keeping `energy` on a wallet would cost — ESTIMATED from the desk's own
 * price board — and what burning the same transfers would.
 *
 * The desk prices a standing figure itself, but only once it is saved; this is
 * what the screen can say before that. The board prices two amounts for a
 * standing rental, and the rate falls with size (on 3 October 43 sun per energy
 * a day at 65,000, and 40 from 1,31,000 all the way to 16,25,000). So it takes
 * the board's row for the largest amount NOT ABOVE this one and scales that
 * row's own cost: it errs high, never low. With no such row there is no
 * estimate — the first version fell back to the smallest row there was, which
 * could be a LARGER amount at a lower rate (a review, 3 October 2026).
 * The length is the row's own, and it must be whole days: those are what the
 * provider bills, and only then is "every N days" both what is paid for and
 * what is covered. Only the row's exact figures are used, and the board's date
 * goes with the answer. Anything missing is "no estimate" (null), never a
 * guess. (The same rule as energy/public/topup.js standingEstimate.)
 */
export function standingEstimate({ energy, prices, energyFeeSun }: {
  energy: string | bigint;
  prices: { at: string; rows: readonly BoardRow[] } | null | undefined;
  energyFeeSun: string | null | undefined;
}): StandingEstimate | null {
  const perDay = sendsADayOf(energy);
  if (perDay === null || !prices || typeof prices.at !== 'string' || !Array.isArray(prices.rows)) return null;
  const digits = (v: unknown): v is string => typeof v === 'string' && /^\d{1,18}$/.test(v);
  const rows = prices.rows.filter((r) => r && r.purpose === 'standing' && r.problem === null
    && digits(r.costSun) && digits(r.energy) && BigInt(r.energy) > 0n && Number.isInteger(r.durationSec) && r.durationSec > 0);
  if (rows.length === 0) return null;
  const kept = BigInt(perDay) * SEND_ENERGY;
  const sorted = [...rows].sort((a, b) => (BigInt(a.energy) < BigInt(b.energy) ? -1 : BigInt(a.energy) > BigInt(b.energy) ? 1 : 0));
  const row = sorted.filter((r) => BigInt(r.energy) <= kept).at(-1);
  if (!row || row.durationSec % 86_400 !== 0) return null;
  const rowEnergy = BigInt(row.energy);
  const costSun = (kept * BigInt(row.costSun as string) + rowEnergy - 1n) / rowEnergy;   // rounded UP: an estimate errs high
  const days = BigInt(row.durationSec / 86_400);
  const sends = BigInt(perDay) * days;
  const burnSun = digits(energyFeeSun) && BigInt(energyFeeSun) > 0n ? sends * SEND_ENERGY * BigInt(energyFeeSun) : null;
  return { costSun, days: Number(days), sends: Number(sends), perSendSun: (costSun + sends - 1n) / sends, burnSun, atIso: prices.at };
}

/**
 * What was priced, kept WITH its price and the key minted for it — and WHEN
 * the price came back, on the viewer's clock: a panel can be closed and opened
 * again long after, and a quote is not shown without its date.
 */
export interface TopUpQuoted<Q> {
  asked: string;
  /** The term priced, in seconds; null = the desk's default. */
  term: number | null;
  key: string;
  quote: Q;
  atMs: number;
  /**
   * The rental the desk's list showed THIS order as, once it has (the order's answer was lost; the list, read again, held it) — a
   * fact about the key that nothing later makes untrue. It lives and goes with the quote: an edit, a new price or an answer drops
   * both. A notice about it can be put away ("Noted") and the rental can leave the list's recent window once it is settled; the
   * panel goes on knowing which rental its order is (Astra, 23 Oct 2026).
   */
  rentalId: number | null;
}

export interface TopUpState<Q> {
  target: TopUpTarget;
  other: string;
  /** How long it is rented for, in seconds; null = the desk's default term. Part of what is priced: changing it drops the price. */
  term: number | null;
  /** Counts the edits. A price is asked AT an edit and kept only if it is still that edit when it comes back. */
  edits: number;
  /** The edit a price is being asked at; null = no price is on its way. */
  pricingAt: number | null;
  quoted: TopUpQuoted<Q> | null;
  /** An order is on its way. Until it is answered — or is not — nothing on the panel moves. */
  sending: boolean;
  /**
   * The order just sent got NO answer (the reason, in words). While this is
   * set the quote and its key are still here: sending again sends the SAME order.
   */
  unanswered: string | null;
  outcome: OrderOutcome | null;
  /** When the last order's answer — or its silence — was recorded, on the viewer's clock. What the panel says about an order, it says with this. */
  saidAtMs: number | null;
  problem: string | null;
}

export type TopUpAction<Q> =
  | { type: 'edit'; target?: TopUpTarget; other?: string; term?: number | null }
  | { type: 'pricing' }
  /** `atMs`: when the price came back, on the viewer's clock — a reducer reads no clock of its own. */
  | { type: 'priced'; at: number; asked: string; term: number | null; key: string; quote: Q; atMs: number }
  | { type: 'not_priced'; at: number | null; problem: string }
  | { type: 'send' }
  | { type: 'answered'; outcome: OrderOutcome; atMs: number }
  /** `atMs`: when it was given up on. */
  | { type: 'unanswered'; why: string; atMs: number }
  /** The desk's list showed the order under `key` as rental `rentalId`. */
  | { type: 'recovered'; key: string; rentalId: number };

export function topUpInitial<Q>(): TopUpState<Q> {
  return { target: '65000', other: '', term: null, edits: 0, pricingAt: null, quoted: null, sending: false, unanswered: null, outcome: null, saidAtMs: null, problem: null };
}

/** What is being asked for, as digits — or as whatever was typed, when that is not digits. */
export function topUpAsked<Q>(s: TopUpState<Q>): string {
  return s.target === 'other' ? s.other.replace(/[,\s]/g, '') : s.target;
}

/** Is what was typed an amount that can be priced? */
export function topUpAskable(asked: string): boolean {
  return /^\d{1,9}$/.test(asked) && Number(asked) > 0;
}

export function topUpReduce<Q>(s: TopUpState<Q>, a: TopUpAction<Q>): TopUpState<Q> {
  switch (a.type) {
    case 'edit':
      // NOT WHILE AN ORDER IS ON ITS WAY. The quote and key on the panel are
      // that order's; dropping them now would leave its answer — or its
      // silence — with nothing to be shown against.
      if (s.sending) return s;
      return {
        ...s,
        target: a.target ?? s.target,
        other: a.other ?? s.other,
        term: a.term !== undefined ? a.term : s.term,
        edits: s.edits + 1,
        // The quote on screen was for something else, and goes — with what was
        // said about it, including "sending again sends the same order".
        // (That an order may have been placed is not kept HERE at all: see PART THREE.)
        pricingAt: null, quoted: null, unanswered: null, outcome: null, saidAtMs: null, problem: null,
      };

    case 'pricing':
      if (s.sending) return s;
      // A new price is a new order: the old quote, its key and anything said
      // about it go now, not when the new price arrives.
      return { ...s, pricingAt: s.edits, quoted: null, unanswered: null, outcome: null, saidAtMs: null, problem: null };

    case 'priced':
      // Kept only if it is still the answer to the question being asked: the
      // same edit, and still waited for. (An order on its way has nothing
      // being asked: asking for a price drops the quote, and without a quote
      // nothing can be sent.)
      if (a.at !== s.edits || s.pricingAt !== a.at) return s;
      // The term priced goes WITH the price, as the amount does: the quote is for both. And the panel keeps it as its
      // own from now on — a panel left on "the desk's default" would show a NEW default after a refresh beside a
      // button still sending the one priced (a review, 18 Oct 2026).
      return { ...s, term: s.term ?? a.term ?? null, pricingAt: null, quoted: { asked: a.asked, term: a.term ?? null, key: a.key, quote: a.quote, atMs: a.atMs, rentalId: null }, problem: null };

    case 'not_priced':
      // `at: null` — it was never asked (what was typed is not an amount).
      if (s.sending) return s;
      if (a.at !== null && (a.at !== s.edits || s.pricingAt !== a.at)) return s;
      return { ...s, pricingAt: null, quoted: null, problem: a.problem };

    case 'send':
      if (s.sending || s.quoted === null) return s;
      return { ...s, sending: true, unanswered: null, outcome: null, saidAtMs: null, problem: null };

    case 'answered':
      if (!s.sending) return s;
      // The desk has ANSWERED: that key is spent — sent again it would only be
      // told the same thing. The quote goes, whatever the answer was.
      return { ...s, sending: false, quoted: null, unanswered: null, outcome: a.outcome, saidAtMs: a.atMs };

    case 'unanswered':
      if (!s.sending) return s;
      // Nothing came back. The quote and its key STAY, so the same order can be sent again.
      return { ...s, sending: false, unanswered: a.why, saidAtMs: a.atMs };

    case 'recovered':
      // Only the order on the panel NOW can be told of it: a panel that has moved on to another quote is not about this key.
      if (s.quoted === null || s.quoted.key !== a.key || s.quoted.rentalId === a.rentalId) return s;
      return { ...s, quoted: { ...s.quoted, rentalId: a.rentalId } };

    default:
      return s;
  }
}

/** The order — from what was priced, and from nothing else. Null when nothing on the panel can be sent. */
export function topUpOrder<Q extends { ok: boolean; nothingToDo: boolean; costSun: string | null; durationSec?: number }>(s: TopUpState<Q>, walletId: number):
  { requestKey: string; walletId: number; purpose: 'top_up'; energy: string; durationSec?: number; maxCostSun: string } | null {
  if (s.sending || s.quoted === null) return null;
  const { asked, term, key, quote } = s.quoted;
  // Only a quote that says "yes, and it costs this" can be ordered: not a
  // refusal, not "nothing to rent", and not a yes with no price — a price is
  // digits, and without one there is nothing that was agreed to.
  if (quote.ok !== true || quote.nothingToDo === true) return null;
  if (typeof quote.costSun !== 'string' || !/^\d+$/.test(quote.costSun)) return null;
  // A term was asked for, and the price is for some other one: nothing that was agreed to.
  if (term != null && quote.durationSec !== term) return null;
  return { requestKey: key, walletId, purpose: 'top_up', energy: asked, ...(term != null ? { durationSec: term } : {}), maxCostSun: quote.costSun };
}

/**
 * Why a top-up may not be started — the wallet not active, or the Books'
 * register not vouching for it (`why`) — or null when it may. NEVER for
 * sending again an order that got no answer: the desk answers a request it
 * already has with what became of it before it looks at anything else, and
 * that answer is the only one that can say whether it was placed (operator,
 * 4 Oct 2026, D6; a review, 5 Oct: the panel had been locked away from it).
 * The panel's state outlives the panel, so this holds after it is closed and
 * opened again.
 */
export function topUpHeldBack<Q>(s: TopUpState<Q> | undefined, why: string | null): string | null {
  if (s !== undefined && s.unanswered !== null && s.quoted !== null) return null;
  return why;
}

// =========================================================================
// PART THREE — an order that got no answer is remembered BY ITS KEY.
//
// "It may have been placed" is a fact about one order. It is not ended by an
// edit, by closing a panel, or by the answer to some OTHER order — the first
// versions ended it on each of those. (A refusal of order B says nothing about
// whether order A was charged; a review ran exactly that.) What ends it:
//
//   - the desk ANSWERS that key (it was sent again, and the desk said what it
//     had made of it); or
//   - the desk's own list shows a rental under that key — it arrived, and here
//     is what it became; or
//   - the operator says "noted" — offered only once the desk's list has been
//     read again, by a request made AFTER the order was given up on.
//
// WHAT THE SCREEN NEVER DOES is decide for itself that the order was not
// placed. All it could go on is a list that does not show it — and that list
// is the newest forty rentals, as they stood at one moment: an order still
// waiting its turn at the desk is not on it yet. So the screen says what it
// saw and when, and the conclusion is the operator's. (The version before this
// one announced "never reached the desk" three minutes on. Nothing made three
// minutes the right number.)
//
// The memory lasts as long as the page: a reload forgets the reminder. It does
// not forget the order — the desk's record is where that is, and the list on
// this screen is read from it.
// =========================================================================

export type OrderPurpose = 'top_up' | 'standing';
/**
 * `failedAtMs`   when it was given up on, on the viewer's clock.
 * `checkedAtMs`  when the desk's list was last ASKED FOR after that — and did
 *                not show it. Null until it has been.
 */
export interface LostOrder { key: string; walletId: number; purpose: OrderPurpose; failedAtMs: number; checkedAtMs: number | null }
/**
 * A lost order that the desk's list turned out to hold: it arrived, and is that
 * rental. NOT what state it is in — that changes, and a copy kept here would go
 * on saying "no answer" of a rental long since delivered. The screen reads the
 * state from the list it is showing. (A review, 1 October 2026.)
 */
export interface FoundOrder { key: string; walletId: number; purpose: OrderPurpose; rentalId: number }

/**
 * What a reading of the desk's list says about each order that got no answer.
 *
 * @param askedAtMs when that reading was ASKED FOR, on the viewer's clock — not
 *                  when it arrived. A reading already on its way when the order
 *                  went out shows the desk as it was before the order.
 * @returns `lost` is the SAME ARRAY when the reading changed nothing.
 */
export function reconcileLost(lost: LostOrder[], rentals: ReadonlyArray<{ requestKey: string; id: number }>, askedAtMs: number):
  { lost: LostOrder[]; found: FoundOrder[] } {
  const still: LostOrder[] = [];
  const found: FoundOrder[] = [];
  let moved = false;
  for (const l of lost) {
    const rental = rentals.find((r) => r.requestKey === l.key);
    if (rental) {
      found.push({ key: l.key, walletId: l.walletId, purpose: l.purpose, rentalId: rental.id });
      moved = true;
    } else if (askedAtMs > l.failedAtMs && (l.checkedAtMs === null || askedAtMs > l.checkedAtMs)) {
      // Looked, and it is not there. (A time that is not a number is not "after".)
      still.push({ ...l, checkedAtMs: askedAtMs });
      moved = true;
    } else still.push(l);
  }
  return { lost: moved ? still : lost, found };
}


// =========================================================================
// PART FOUR — the screen's memory of orders, above the panels.
//
// One state per wallet for the top-up panel and for "arrange it now", plus the
// orders that got no answer. Held in a store outside any component, so closing
// a panel, opening another wallet's, turning the phone (the table and the cards
// are two renderings of one panel) or leaving the screen and coming back does
// not forget an order that is on its way — or one that may have been placed.
// =========================================================================

/**
 * "Arrange it now": one key per arrangement, kept until the desk answers it.
 *
 * THE KEY BELONGS TO THE FIGURE IT WAS SENT FOR. A standing order carries no
 * size — the desk takes it from the wallet's saved allocation — so the same
 * key means "the same order" only while that allocation is the same. A review
 * (1 October 2026) arranged 65,000, lost the answer, saved 1,31,000 and pressed
 * again: the button named 1,31,000, the desk answered with the rental it had
 * already made for 65,000, and the screen said "Ordered". So `figure` is kept
 * with the key, and a press for another figure is a NEW order under a new key.
 * (The one that got no answer is still remembered, by its key: PART THREE.)
 */
export interface ArrangeState {
  key: string | null;
  /** The saved allocation the key was sent for, as digits. */
  figure: string | null;
  sending: boolean;
  unanswered: string | null;
  outcome: OrderOutcome | null;
  /** When the answer — or the silence — was recorded, on the viewer's clock. */
  saidAtMs: number | null;
}
export type ArrangeAction =
  /** `key`: a freshly minted one — used unless an arrangement OF THIS FIGURE is still waiting for an answer. `figure`: the saved allocation, as digits. */
  | { type: 'send'; key: string; figure: string }
  | { type: 'answered'; outcome: OrderOutcome; atMs: number }
  | { type: 'unanswered'; why: string; atMs: number }
  /** The figure was edited or saved: what was said about the last arrangement no longer describes what is on screen. */
  | { type: 'clear' };

export const arrangeInitial = (): ArrangeState => ({ key: null, figure: null, sending: false, unanswered: null, outcome: null, saidAtMs: null });

/** Would pressing "arrange" for this figure send the SAME order again? Only if one for this very figure is still waiting for an answer. */
export function arrangeRetries(s: ArrangeState, figure: string): boolean {
  return s.key !== null && s.unanswered !== null && s.figure === figure;
}

/** The key the next "arrange" of this figure goes out under: the one still waiting for an answer — if it was for this figure — or the new one. */
export function arrangeKeyOf(s: ArrangeState, fresh: string, figure: string): string {
  return s.key !== null && s.figure === figure ? s.key : fresh;
}

export function arrangeReduce(s: ArrangeState, a: ArrangeAction): ArrangeState {
  switch (a.type) {
    case 'send':
      if (s.sending) return s;
      return { key: arrangeKeyOf(s, a.key, a.figure), figure: a.figure, sending: true, unanswered: null, outcome: null, saidAtMs: null };
    case 'answered':
      if (!s.sending) return s;
      // Answered: that key is spent.
      return { key: null, figure: null, sending: false, unanswered: null, outcome: a.outcome, saidAtMs: a.atMs };
    case 'unanswered':
      if (!s.sending) return s;
      // The key is KEPT, with its figure: again — for that figure — means the same order.
      return { ...s, sending: false, unanswered: a.why, saidAtMs: a.atMs };
    case 'clear':
      // Only what was said about an ANSWERED arrangement goes. One that got no
      // answer keeps its key, its figure and its date; one on its way has
      // nothing said about it yet. (Neither has an outcome.)
      if (s.outcome === null) return s;
      return { ...s, outcome: null, saidAtMs: null };
    default:
      return s;
  }
}

export interface EnergyOrders<Q> {
  topUps: Record<number, TopUpState<Q>>;
  arranges: Record<number, ArrangeState>;
  lost: LostOrder[];
  found: FoundOrder[];
}

export type EnergyOrdersAction<Q> =
  | { type: 'top_up'; walletId: number; action: TopUpAction<Q> }
  | { type: 'arrange'; walletId: number; action: ArrangeAction }
  /** A reading of the desk's list. `askedAtMs`: when it was ASKED FOR, on the viewer's clock. */
  | { type: 'desk_record'; rentals: ReadonlyArray<{ requestKey: string; id: number }>; askedAtMs: number }
  | { type: 'dismiss'; key: string };

export function ordersInitial<Q>(): EnergyOrders<Q> {
  return { topUps: {}, arranges: {}, lost: [], found: [] };
}

/**
 * This order got no answer — NOW. If it was already on the list (it was sent
 * again, and again nothing came back), it starts over: whatever the desk's
 * list said before this attempt says nothing about this one.
 */
const withLost = (lost: LostOrder[], order: LostOrder): LostOrder[] => [...lost.filter((l) => l.key !== order.key), order];

/**
 * The quotes the panels hold, told which rental the desk's list shows for each — by the order's own key, whatever the screen's
 * notices say. The SAME object when no quote learns anything. (A key is minted for one quote; a rental under it is that order.)
 */
function quotesLearnRentals<Q>(topUps: Record<number, TopUpState<Q>>, rentals: ReadonlyArray<{ requestKey: string; id: number }>): Record<number, TopUpState<Q>> {
  let out = topUps;
  for (const [wallet, panel] of Object.entries(topUps)) {
    const key = panel.quoted?.key;
    const rental = key === undefined ? undefined : rentals.find((r) => r.requestKey === key);
    if (key === undefined || rental === undefined) continue;
    const told = topUpReduce(panel, { type: 'recovered', key, rentalId: rental.id });
    if (told !== panel) out = { ...out, [wallet]: told };
  }
  return out;
}

export function ordersReduce<Q>(s: EnergyOrders<Q>, a: EnergyOrdersAction<Q>): EnergyOrders<Q> {
  switch (a.type) {
    case 'top_up': {
      const before = s.topUps[a.walletId] ?? topUpInitial<Q>();
      const after = topUpReduce(before, a.action);
      let lost = s.lost;
      // The key of the order that was on its way — if one was.
      const key = before.sending ? (before.quoted?.key ?? null) : null;
      if (key !== null && a.action.type === 'unanswered') lost = withLost(lost, { key, walletId: a.walletId, purpose: 'top_up', failedAtMs: a.action.atMs, checkedAtMs: null });
      // An answer settles THE ORDER IT ANSWERS, and no other.
      if (key !== null && a.action.type === 'answered') lost = lost.filter((l) => l.key !== key);
      // Nothing changed — the same object, so that nothing is redrawn for it.
      if (after === before && lost === s.lost) return s;
      return { ...s, topUps: { ...s.topUps, [a.walletId]: after }, lost };
    }
    case 'arrange': {
      const before = s.arranges[a.walletId] ?? arrangeInitial();
      const after = arrangeReduce(before, a.action);
      let lost = s.lost;
      const key = before.sending ? before.key : null;
      if (key !== null && a.action.type === 'unanswered') lost = withLost(lost, { key, walletId: a.walletId, purpose: 'standing', failedAtMs: a.action.atMs, checkedAtMs: null });
      if (key !== null && a.action.type === 'answered') lost = lost.filter((l) => l.key !== key);
      if (after === before && lost === s.lost) return s;
      return { ...s, arranges: { ...s.arranges, [a.walletId]: after }, lost };
    }
    case 'desk_record': {
      // EVERY reading, whatever is on the list of orders that got no answer: a quote on a panel learns its rental the first time the
      // list shows it — with or without a notice for it. (The reminder may have been put away, "Noted", BEFORE the list showed the
      // rental, and then nothing here would ever have told the quote: Astra, 23 Oct 2026, round 1d.)
      const topUps = quotesLearnRentals(s.topUps, a.rentals);
      const learned = topUps === s.topUps ? s : { ...s, topUps };
      if (s.lost.length === 0) return learned;
      const { lost, found } = reconcileLost(s.lost, a.rentals, a.askedAtMs);
      if (lost === s.lost) return learned;
      // One notice per order: a rental found again (the order was sent again, and lost again) replaces what was said before.
      const keys = new Set(found.map((x) => x.key));
      return { ...learned, lost, found: [...s.found.filter((x) => !keys.has(x.key)), ...found] };
    }
    case 'dismiss': {
      const found = s.found.filter((x) => x.key !== a.key);
      // A reminder is put away only once the desk's list has been read since — not before there is anything to go on.
      const lost = s.lost.filter((l) => !(l.key === a.key && l.checkedAtMs !== null));
      if (found.length === s.found.length && lost.length === s.lost.length) return s;
      return { ...s, lost, found };
    }
    default:
      return s;
  }
}

/** A store outside any component: `get` the state, `dispatch` an action, `subscribe` to changes (returns the unsubscribe). */
export function createStore<S, A>(reduce: (state: S, action: A) => S, initial: S):
  { get: () => S; dispatch: (action: A) => void; subscribe: (listener: () => void) => () => void } {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    dispatch: (action) => {
      const next = reduce(state, action);
      if (next === state) return;
      state = next;
      for (const listener of [...listeners]) listener();
    },
    subscribe: (listener) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
  };
}

// =========================================================================
// PART FIVE — the desk's own sentences, as the line the screen shows.
//
// The alarm sentences and the notes on a rental are the DESK's (energy/src/alarm.mjs, desk.mjs) and the refusals the Books'
// (bff/src/lib/energy.ts): whole sentences, up to fifty words. The screen shows ONE line and keeps the whole sentence behind a
// tap (design-system sweep, 22 Oct 2026 — "too much descriptive everything you are building"). Two rules make that safe:
//
//   - THE LINE NEVER DROPS THE CONSEQUENCE. An alarm that says "sends will burn TRX instead" or "nothing is being followed up,
//     renewed or read", or that a provider "may have charged", is cut so that THAT clause stays on the line: whatever else is
//     left for the tap, it is not that. (CONSEQUENCE below; tests/energy-screen pins each shape the desk writes.)
//   - NOTHING IS LOST. `folded` says there is more than the line, and the screen then offers the whole sentence as the desk
//     wrote it. A line is a prefix or a summary of the sentence — never a different statement.
//
// No rule about an order lives here: nothing in this part decides what may be ordered, held back or settled.
// =========================================================================

/** Words as the screen counts them: a run of characters with a letter or a digit in it. */
export function wordsIn(s: string): number {
  return s.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
}

/** A text's sentences: cut after . ! ? when the next one starts with a capital, a digit or a #. */
export function sentencesOf(text: string): string[] {
  return text.trim().split(/(?<=[.!?])\s+(?=[A-Z0-9#])/).map((s) => s.trim()).filter((s) => s !== '');
}

/** A line, and whether the sentence it came from has more than that line says. */
export interface Led { lead: string; folded: boolean }

const unstop = (s: string): string => s.replace(/[.:;,\s]+$/, '');
const stop = (s: string): string => (/[.!?]$/.test(s) ? s : `${s}.`);
/** Same text, ignoring its closing stop and spaces: is the line the whole sentence? */
const sameText = (a: string, b: string): boolean => unstop(a) === unstop(b);

/**
 * The first clause of a reason the desk or the Books gave: its first sentence, and — when that runs past `max` words — cut at
 * " — ", ": " or " (" where the cut leaves at least three words. A sentence with no such place is shown whole.
 */
export function reasonLead(text: string, max = 12): Led {
  const t = text.trim();
  const first = sentencesOf(t)[0] ?? t;
  let lead = first;
  if (wordsIn(first) > max) {
    for (const at of [' — ', ': ', ' (']) {
      let i = first.indexOf(at);
      while (i !== -1 && wordsIn(first.slice(0, i)) < 3) i = first.indexOf(at, i + 1);
      if (i !== -1 && wordsIn(first.slice(0, i)) <= max + 4) { lead = first.slice(0, i); break; }
    }
  }
  return { lead: stop(unstop(lead)), folded: !sameText(lead, t) };
}

/**
 * What each alarm's line must still say when the sentence is cut: the CONSEQUENCE. Float and burn: that sends burn TRX
 * instead; the desk's rounds: that nothing is followed up, renewed or read; an order with no answer: that it may have
 * been charged. (energy/src/alarm.mjs says each in the sentence AFTER the one that names the fact.) Used for a shape the
 * summaries below do not know.
 */
const CONSEQUENCE: Record<string, RegExp> = {
  float: /\bsends (?:will burn|are burning) TRX instead/i,
  burn: /\bsends (?:will burn|are burning) TRX instead/i,
  desk: /\bnothing is being followed up/i,
  unconfirmed: /\bmay have charged\b/i,
};

/** The ids an alarm's sentence names: "(#44, #46)" → ["#44", "#46"]. */
const idsIn = (s: string): string[] => [...s.matchAll(/#\d+/g)].map((m) => m[0]);
const listIds = (ids: string[], noun: string): string => (ids.length <= 3 ? ids.join(', ') : `${ids.length} ${noun}`);
/**
 * A wallet's name in the bible's list form (docs/TERMINOLOGY.md §2, N-091: "In a list: one line: `Hot TRC20 Oct`"): a name of
 * the register's shape — Group · Network, and a Tag, then its register number (4 Nov 2026: "Hot · TRC20 #57", "Hot · TRC20 · Nov
 * #53") — said without its dots; any other name as it is.
 */
const N091_NAME = /^(?:Treasury|Investment|Hot|Merchant Hot|Deposit|Fee|Flagged|[^·]+ deposit) · (?:TRC20|ERC20|BEP20|Base|SOL|BTC|Aptos|TON)(?: · [^·]+)?(?: #\d+)?$/;
export function listName(name: string): string {
  const n = name.trim();
  return N091_NAME.test(n) ? n.split(' · ').join(' ') : n;
}

/** A name's words, its register number not counted (4 Nov 2026): "Hot TRC20 Nov #53" is three, as "Hot TRC20 Nov" was. */
const nameWords = (name: string): number => wordsIn(name.replace(/\s#\d+\b/g, ''));
/** Names a line may carry: a short list is named; a long one is not (the row says which, and so does the tap). Short is up to
 *  three words. */
const shortNames = (names: string): string | null => { const n = listName(names); return nameWords(n) <= 3 ? n : null; };

const STANDING_PREFIX = 'The standing allocation is not in place for ';
/**
 * The wallets a "standing allocation is not in place" alarm names — from the COMPLETE alarm. The desk writes "A (its note); B (its
 * note)." and a note is a sentence of its own, often several ("nothing was ordered for A: … Ask again."): the first sentence of
 * the alarm ends inside the first note, so a count taken from it loses every wallet after the first (Astra, 23 Oct 2026). The
 * entries are split at "; " outside parentheses, the label of each is what stands before its note's own parentheses.
 * Null when that cannot be done with certainty — an unbalanced parenthesis, an entry that is not "label (note)" — and then the
 * line says no number at all.
 */
function standingWallets(alarmText: string): string[] | null {
  const t = alarmText.trim();
  if (!t.startsWith(STANDING_PREFIX) || !t.endsWith('.')) return null;
  const body = t.slice(STANDING_PREFIX.length, -1);
  const entries: string[] = [];
  let depth = 0;
  let from = 0;
  for (let i = 0; i < body.length; i++) {
    const c = body[i];
    if (c === '(') depth++;
    else if (c === ')') { depth--; if (depth < 0) return null; }
    else if (c === ';' && depth === 0 && body[i + 1] === ' ') { entries.push(body.slice(from, i)); from = i + 2; }
  }
  if (depth !== 0) return null;
  entries.push(body.slice(from));
  const labels: string[] = [];
  for (const entry of entries) {
    if (!entry.endsWith(')')) return null;
    // the parenthesis that opens the note is the one that closes at the end of the entry
    let nest = 0;
    let open = -1;
    for (let i = entry.length - 1; i >= 0 && open === -1; i--) {
      if (entry[i] === ')') nest++;
      else if (entry[i] === '(' && --nest === 0) open = i;
    }
    const label = open > 0 ? entry.slice(0, open).trim() : '';
    if (label === '') return null;
    labels.push(label);
  }
  return labels;
}

/**
 * What an alarm is ABOUT, as the desk says it (alarm.walletIds) and the screen resolves it against the wallet records: identity, not
 * read out of a sentence. A wallet's label can hold any punctuation — "Hot (Dubai); reserve" is ONE wallet, and in the alarm's
 * sentence it looks like two — so where the desk says which wallets, nothing is counted out of the text. `label` is null for a
 * wallet the screen has no record of.
 */
export interface AlarmAbout { wallets: ReadonlyArray<{ id: number; label: string | null }> }

/**
 * What an alarm is about: its `walletIds` (energy/src/alarm.mjs puts them on every alarm that is about wallets; the status answer
 * carries them), resolved against the wallet records. They arrive as data from outside — a list of whole numbers is read, anything
 * else is nothing, and an alarm about nothing the desk said gives null (the line then goes by the sentence alone).
 */
export function alarmAbout(alarm: { walletIds?: unknown }, wallets: ReadonlyArray<{ id: number; label: string }>): AlarmAbout | null {
  const ids = alarm.walletIds;
  if (!Array.isArray(ids) || ids.length === 0 || !ids.every((id) => Number.isInteger(id))) return null;
  return { wallets: (ids as number[]).map((id) => ({ id, label: wallets.find((w) => w.id === id)?.label ?? null })) };
}

/**
 * THE KNOWN SHAPES. The desk writes ten alarms (energy/src/alarm.mjs), each in a few shapes; each shape has a summary of at most
 * ten words that says the FACT and, where the alarm has one, the CONSEQUENCE ("sends burn TRX instead", "the desk does not
 * order", "nothing is followed up, renewed or read", "may have been charged"). The whole sentence is behind the tap. A label
 * is put on the line only when it is short — a long one would push the line over its ten words. tests/energy-screen runs the
 * desk's own assessAlarms over every shape and counts the words of what comes out.
 */
type Shape = (first: string, whole: string, about: AlarmAbout | null) => string | null;
const SHAPES: Record<string, Shape[]> = {
  float: [
    (f) => (/^No TronSave API key is configured/i.test(f) ? 'No TronSave key — the desk cannot order.' : null),
    (f) => (/^The (?:prepaid|TronSave) balance has never been read/i.test(f) ? 'Balance never read — the desk will not order.' : null),
    // A balance a hair under a line is "just under" it, and one too small to show is "under 0.01" (energy/src/pricing.mjs trx, hair).
    (f) => {
      const m = /^The (?:prepaid|TronSave) balance is (?:(.+?) TRX — under|just under) the \S+ TRX floor/i.exec(f);
      if (!m) return null;
      const fig = m[1];
      return `Balance ${fig === undefined ? 'just under the floor' : /^under /.test(fig) ? `${fig} TRX` : `${fig} TRX, under the floor`} — sends burn TRX instead.`;
    },
    (f, w) => (/^The (?:prepaid|TronSave) balance could not be read/i.test(f)
      ? (/sends are burning TRX instead/i.test(w) ? 'Balance unread — no orders; sends are burning TRX instead.' : 'Balance unread — the desk does not order.') : null),
    (f) => { const m = /^The (?:prepaid|TronSave) balance is (?:(.+?) TRX — under|just under) (\S+) TRX\.?$/i.exec(f); return m ? `Balance ${m[1] === undefined ? 'just' : `${m[1]} TRX,`} under ${m[2]} TRX.` : null; },
  ],
  undelivered: [
    (f) => {
      const m = /^(\d+) orders? not delivered after (\d+) minutes?/i.exec(f);
      if (!m) return null;
      const ids = idsIn(f);
      return `${m[1]} ${m[1] === '1' ? 'order' : 'orders'} not delivered after ${m[2]} min${ids.length > 0 && ids.length <= 3 ? ` (${ids.join(', ')})` : ''}.`;
    },
  ],
  chain: [
    (f) => {
      const m = /^(No wallet|\d+ wallets?) (can|cannot) be read from the chain/i.exec(f);
      if (!m) return null;
      const who = m[1] === 'No wallet' ? 'no orders' : `no orders for ${m[1] === '1 wallet' ? 'it' : 'them'}`;
      return `${m[1]} ${m[2]} be read — ${who}.`;
    },
    // The names come first and are read from the WHOLE alarm: a label may hold a full stop ("St. Mary payouts"), and the first sentence ends there.
    (_f, w) => { const m = /^(.+?) (?:has|have) not been read from the chain yet/.exec(w); if (!m) return null; const n = shortNames(m[1] ?? ''); return n ? `${n}: not read from the chain yet.` : 'Wallets not read from the chain yet.'; },
  ],
  dormant: [
    (_f, w) => { const m = /^(.+?) (?:has|have) never been activated on-chain and (?:is|are) not being activated/.exec(w); if (!m) return null; const n = shortNames(m[1] ?? ''); return n ? `Not being activated: ${n}.` : 'Wallets not being activated on-chain.'; },
    (_f, w) => { const m = /^(.+?) (?:has|have) never been activated on-chain — energy cannot be delegated/.exec(w); if (!m) return null; const n = shortNames(m[1] ?? ''); return n ? `Not activated on-chain: ${n} — send it TRX.` : 'Wallets not activated — send them TRX.'; },
  ],
  burn: [
    (f) => (/^The chain's burn price has not been read yet/i.test(f) ? 'Burn price not read yet — the desk does not order.' : null),
    (f, w) => (/^The chain's burn price could not be read/i.test(f)
      ? (/sends are burning TRX instead/i.test(w) ? 'Burn price unread — no orders; sends are burning TRX instead.' : 'Burn price unread — the desk does not order.') : null),
  ],
  standing: [
    (_f, w, about) => {
      if (!w.trim().startsWith(STANDING_PREFIX)) return null;
      // The wallets the desk says it is about: counted and named from the records, never from the sentence.
      if (about !== null && about.wallets.length > 0) {
        const n = about.wallets.length;
        const only = n === 1 ? (about.wallets[0]?.label ?? '') : '';
        return only !== '' && nameWords(only) <= 4 ? `Standing figure not in place: ${only}.` : `Standing figure not in place for ${n} wallet${n === 1 ? '' : 's'}.`;
      }
      // Without that, the sentence alone — the WHOLE alarm, never its first sentence (a note ends in ". Ask again." and a second wallet
      // follows it). One entry is one wallet; two or more cannot be told from ONE label that holds a "; ": no number is given.
      const labels = standingWallets(w);
      if (labels === null || labels.length !== 1) return 'Standing figure not in place for some wallets.';
      const only = labels[0] as string;
      return nameWords(only) <= 4 ? `Standing figure not in place: ${only}.` : 'Standing figure not in place for 1 wallet.';
    },
  ],
  spend: [
    (f, w) => {
      const m = /^The last 24 hours' spend is (\S+) of the (\S+) TRX daily limit/i.exec(f);
      if (!m) return null;
      return /no more orders/i.test(w) ? 'Daily limit reached — no more orders for now.' : `24h spend ${m[1]} of ${m[2]} TRX.`;
    },
  ],
  desk: [
    (f) => (/^The desk has not completed a pass yet/i.test(f) ? 'Desk has not completed a pass yet — nothing is checked.' : null),
    (f) => { const m = /^The desk last completed a pass (\d+) min ago/i.exec(f); return m ? `Desk idle ${m[1]} min — nothing followed up, renewed or read.` : null; },
    (f) => (/^Part of the last pass failed/i.test(f) ? 'Part of the desk\'s last pass failed.' : null),
  ],
};

/**
 * An alarm's sentence as ONE line of at most ten words. A shape the desk is known to write gets its summary (SHAPES); any other
 * is its first sentence — a parenthesis of 25 characters or more dropped when it runs past twelve words — with, where the alarm has
 * one, the clause that says what it costs (CONSEQUENCE). "N orders went out and no answer came back (#a, #b)" is "No answer on
 * #a, #b — may have been charged." whatever follows it. Whatever the line leaves out is behind the tap (`folded`).
 */
export function alarmLead(key: string, text: string, about: AlarmAbout | null = null): Led {
  const t = text.trim();
  const sentences = sentencesOf(t);
  let first = sentences[0] ?? t;
  const ids = idsIn(first);
  if (key === 'unconfirmed' && ids.length > 0) return { lead: `No answer on ${listIds(ids, 'orders')} — may have been charged.`, folded: true };
  // "The provider reports 1 rental delivered, but the energy has not shown on the wallet (#41)." — the rental row says the same; the line is short.
  if (key === 'unseen' && ids.length > 0) return { lead: `Delivered but not on the wallet: ${listIds(ids, 'rentals')}.`, folded: true };
  for (const shape of SHAPES[key] ?? []) {
    const lead = shape(first, t, about);
    if (lead !== null) return { lead, folded: !sameText(lead, t) };
  }
  if (wordsIn(first) > 12) first = first.replace(/\s*\([^()]{25,}\)/g, '');
  let lead = unstop(first);
  const keep = CONSEQUENCE[key];
  if (keep !== undefined && !keep.test(first)) {
    const later = sentences.slice(1).find((s) => keep.test(s));
    const m = later === undefined ? null : keep.exec(later);
    if (later !== undefined && m !== null) {
      const before = later.slice(0, m.index);
      // the clause that carries it: after the last " — ", ": " or " and " in front of the match — or the whole sentence
      const from = Math.max(...[' — ', ': ', ' and '].map((d) => (before.lastIndexOf(d) === -1 ? -1 : before.lastIndexOf(d) + d.length)));
      lead = from > 0 ? `${lead}; ${unstop(later.slice(from))}` : `${lead}. ${unstop(later)}`;
    }
  }
  return { lead: stop(lead), folded: !sameText(lead, t) };
}

/**
 * Why the Books' register says no to a wallet, in a few words — the VERDICT, not the party's name or the status code (those are
 * on the row's hover and behind the Edit panel's tap). The Books' sentences are bff/src/lib/energy.ts notLive(); a reason that
 * is none of them is shown as its first clause (reasonLead), never softened.
 */
export function registerWhy(reason: string): string {
  const r = reason.trim();
  if (/^retired in the Books\b/i.test(r)) return 'Retired in the Books';
  if (/^archived in the Books\b/i.test(r)) return 'Archived in the Books';
  if (/^held back by screening\b/i.test(r)) return 'Held back by screening in the Books';
  // The Books' own words (bff/src/lib/energy.ts), as they are and as one name per wallet would have them (N-091, 30 Oct 2026).
  if (/^in the Books as a wallet of .+, not as a (?:treasury|desk) wallet$/i.test(r)) return 'Not a desk wallet in the Books';
  if (/^a wallet of .+, which is not active in the Books$/i.test(r)) return 'Its party is inactive in the Books';
  if (/^not in the Books' (?:register of treasury wallets|wallet register)$/i.test(r)) return 'Not in the Books\' register';
  const lead = reasonLead(r, 10).lead.replace(/\.$/, '');
  return lead.charAt(0).toUpperCase() + lead.slice(1);
}

/** What a note on a rental must not lose when it is cut: that it is counted, or that the desk did not settle it. */
const NOTE_KEEP = [/\bstill counted as spent\b/i, /\bnot settled by the desk itself\b/i];

/**
 * A note the desk wrote on a rental, as one line: whole up to twelve words; longer, cut at the first ": " (after four words or
 * more), then " — ", then ". ", then any ": " — and a phrase of NOTE_KEEP that fell on the far side of the cut is put back.
 * "settled by tg:1001: the provider DID charge for it — …" keeps its verdict: the first colon is after three words, so the cut is
 * at the dash. (Never mid-sentence: a note with no such place is shown whole.)
 */
export function noteLead(detail: string): Led {
  const t = detail.trim();
  if (wordsIn(t) <= 12) return { lead: t, folded: false };
  const cuts: Array<[string, number]> = [[': ', 4], [' — ', 3], ['. ', 3], [': ', 1]];
  for (const [at, min] of cuts) {
    let i = t.indexOf(at);
    while (i !== -1) {
      const n = wordsIn(t.slice(0, i));
      if (n >= min && n <= 18) {
        const lead = unstop(t.slice(0, i));
        const rest = t.slice(i + at.length);
        const kept = NOTE_KEEP.filter((re) => !re.test(lead)).map((re) => re.exec(rest)?.[0]).filter((x): x is string => x !== undefined);
        return { lead: kept.length > 0 ? `${lead} · ${kept.join(' · ').toLowerCase()}` : lead, folded: true };
      }
      i = t.indexOf(at, i + 1);
    }
  }
  return { lead: t, folded: false };
}

/**
 * Was this rental settled by hand as NOT charged? Read from the desk's own first words of its note — "settled by tg:1001: the provider
 * did not charge for it" — and from nothing after them: what follows the dash is free text a person typed. "DID charge" is not it.
 */
export function settledNotCharged(detail: string | null | undefined): boolean {
  return typeof detail === 'string' && /^settled by .{1,40}?: the provider did not charge for it(?!\w)/.test(detail.trim());
}

/**
 * A rental that waits on a person — or on the provider, and not yet seen on its wallet. Such a row is never folded out of the
 * list: it is the first thing the list shows. (The desk's `settleable` is "it cannot settle this one itself".)
 */
export function rentalNeedsHim(r: { state: string; settleable: boolean; chainSeenAt: string | null }): boolean {
  return r.settleable || r.state === 'unconfirmed' || (r.state === 'delegated' && r.chainSeenAt === null);
}

// =========================================================================
// PART SIX — which rental a link is about, whether a recovered order's outcome is known, whether a quote is current.
//
// Three things the screen got wrong in the first sweep (Astra, 22 Oct 2026), each a question with one answer:
//
//   1. A "Settle" link is about ONE rental — the one the alarm names, or the one found on the desk. "The first row that can be
//      settled" is another rental's buttons: the operator closes the wrong one.
//   2. An order that got no answer and was then FOUND on the desk's list is still not known to have been paid for or not: the
//      desk's record of it says "no answer" (or "sending"). Only a record that says what became of it settles that.
//   3. A quote is the price AT A MOMENT. Whether the status polling is healthy says nothing about how old the quote on a panel
//      is: it has its own age.
//
// No rule about an order lives here either: nothing decides what may be ordered, held back or settled.
// =========================================================================

/**
 * The rentals an alarm is ABOUT, as the desk says it (`rentalIds`; energy/src/alarm.mjs puts them on every alarm that names rentals):
 * identity, not read out of a sentence — an order's number in prose can be any other rental's ("#46 is a renewal the desk sent itself").
 * They arrive as data from outside, so: `undefined` when the desk does not say (an older desk — the sentence is all there is); `null`
 * when it says something that is not a list of whole numbers (nothing is taken from it, and nothing from the sentence either); else the
 * ids, possibly none.
 */
export function alarmRentalIds(alarm: { rentalIds?: unknown }): number[] | null | undefined {
  const ids = alarm.rentalIds;
  if (ids === undefined) return undefined;
  if (!Array.isArray(ids) || !ids.every((id) => Number.isInteger(id))) return null;
  return ids as number[];
}

/** Does this alarm name that rental? By the desk's `rentalIds` where it says them; by "#id" in the sentence only for a desk that does not. */
export function alarmNamesRental(alarm: { text: string; rentalIds?: unknown }, rentalId: number): boolean {
  const said = alarmRentalIds(alarm);
  if (said !== undefined) return said !== null && said.includes(rentalId);
  return new RegExp(`#${rentalId}\\b`).test(alarm.text);
}

/**
 * The rental a "Settle" link is about: the FIRST rental the alarm names that is on the list on screen — by the desk's own `rentalIds`
 * (alarmRentalIds) where it says them, and for an older desk the first "#id" in the alarm's own first sentence ("…came back (#44, #46).").
 * Null when it names none that is listed — then there is no link, rather than one to another rental.
 */
export function settleTarget(alarmText: string, rentals: ReadonlyArray<{ id: number }>, rentalIds?: unknown): number | null {
  const said = alarmRentalIds({ rentalIds });
  if (said === null) return null;
  if (said !== undefined) return said.find((id) => rentals.some((r) => r.id === id)) ?? null;
  const first = sentencesOf(alarmText)[0] ?? alarmText;
  for (const m of first.matchAll(/#(\d+)/g)) {
    const id = Number(m[1]);
    if (rentals.some((r) => r.id === id)) return id;
  }
  return null;
}

/**
 * Does the desk's record of a rental say what became of the order? The provider took it (ordered, delivered, ended) — paid for
 * when placed; the provider refused it; or it was settled. NOT "requested" (the provider has not answered: money may be about
 * to move) and NOT "unconfirmed" (no usable answer: it MAY have been charged) — nor a state this file does not know.
 */
const OUTCOME_KNOWN = new Set(['ordered', 'delegated', 'expired', 'refused', 'closed']);
export function outcomeKnown(state: string | null | undefined): boolean {
  return typeof state === 'string' && OUTCOME_KNOWN.has(state);
}

/**
 * How long a quote is "the price now": the desk reads its own price board every five minutes (energy/src/desk.mjs
 * PRICE_BOARD_EVERY_SEC), so a quote older than that is older than the board it came from — whatever the polling says.
 */
export const QUOTE_STALE_AFTER_S = 300;
/** A viewer whose clock was set back: how far behind the quote's moment it may read before its age is "unknown". */
const QUOTE_CLOCK_SLACK_S = 60;

/** Is a quote priced at `atMs` no longer the price now, at `nowMs`? A moment that is not a time, or an age that cannot be told, is stale. */
export function quoteStale(atMs: number | null | undefined, nowMs: number): boolean {
  if (typeof atMs !== 'number' || !Number.isFinite(atMs) || !Number.isFinite(nowMs)) return true;
  const age = (nowMs - atMs) / 1000;
  return age > QUOTE_STALE_AFTER_S || age < -QUOTE_CLOCK_SLACK_S;
}
